import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { BoardItem, BoardColumn, ExecutionStatus, StorySummary, SpecSummary } from '../types/board.js';
import type { AssigneeFilterType } from '../components/board/board-constants.js';
import type { BoardUser } from './board-api.js';
import { isAgentAssignee } from '../components/board/board-constants.js';
import * as boardApi from './board-api.js';
import { wsClient } from './websocket-client.js';

export type BoardLoadingState = 'idle' | 'loading' | 'loaded' | 'error';
export type OwnerFilterType = 'all' | number;

let sharedItems: BoardItem[] = [];
let sharedUsers: BoardUser[] = [];
let sharedLoadingState: BoardLoadingState = 'idle';
let sharedError: string | null = null;
let sharedFilterProject: string | null = null;
let sharedFilterAssigneeType: AssigneeFilterType = 'all';
let sharedFilterOwner: OwnerFilterType = 'all';
let sharedSelectedItem: BoardItem | null = null;
let expandedSpecs: Map<string, StorySummary[]> = new Map();
let expandedSpecsLoading: Set<string> = new Set();
let expandedChildren: Set<string> = new Set();

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadBoardItems(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    const [items, users] = await Promise.all([
      boardApi.getBoardItems(),
      sharedUsers.length === 0 ? boardApi.getBoardUsers() : Promise.resolve(sharedUsers),
    ]);
    sharedItems = items;
    sharedUsers = users;
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Board-Items konnten nicht geladen werden';
  }

  notifyHosts();
}

function updateItemExecutionFields(
  ticketId: string,
  updates: { executionStatus?: ExecutionStatus; queuePosition?: number; boardColumn?: BoardColumn; executionError?: string | null },
): void {
  const idx = sharedItems.findIndex(item => item.id === ticketId);
  if (idx === -1) return;
  sharedItems[idx] = { ...sharedItems[idx], ...updates };
  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string; payload: unknown };
  const { type, payload } = detail;

  if (type === 'board:updated' || type === 'goal:updated' || type === 'comment:created' || type === 'comment:deleted') {
    void loadBoardItems();
    return;
  }

  if (type === 'spec:changed') {
    // Reload board to re-enrich goal children; preserve expandedChildren UI state
    void loadBoardItems();
    return;
  }

  // Handle execution:* events - update execution status on board items in real-time
  if (type === 'execution:started') {
    const { ticketId } = payload as { ticketId: string };
    updateItemExecutionFields(ticketId, { executionStatus: 'running', boardColumn: 'in_progress' });
  } else if (type === 'execution:completed') {
    const { ticketId } = payload as { ticketId: string };
    updateItemExecutionFields(ticketId, { executionStatus: 'completed', queuePosition: undefined });
    void loadBoardItems();
  } else if (type === 'execution:failed') {
    const { ticketId, error } = payload as { ticketId: string; error?: string };
    updateItemExecutionFields(ticketId, { executionStatus: 'failed', queuePosition: undefined, executionError: error });
    void loadBoardItems();
  } else if (type === 'execution:queue-changed') {
    const { queuedJobs } = payload as { queuedJobs: Array<{ ticketId: string; position?: number }> };
    for (const job of queuedJobs) {
      updateItemExecutionFields(job.ticketId, { executionStatus: 'queued', queuePosition: job.position });
    }
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class BoardStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get items(): BoardItem[] {
    return sharedItems;
  }

  get users(): BoardUser[] {
    return sharedUsers;
  }

  get filteredItems(): BoardItem[] {
    let items = sharedItems;

    if (sharedFilterProject) {
      items = items.filter(item => item.sourceProject === sharedFilterProject);
    }

    if (sharedFilterAssigneeType === 'user') {
      items = items.filter(item => !isAgentAssignee(item.assignee));
    } else if (sharedFilterAssigneeType === 'agent') {
      items = items.filter(item => isAgentAssignee(item.assignee));
    }

    if (sharedFilterOwner !== 'all') {
      items = items.filter(item => item.userId === sharedFilterOwner);
    }

    return items;
  }

  get loadingState(): BoardLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  get filterProject(): string | null {
    return sharedFilterProject;
  }

  get filterAssigneeType(): AssigneeFilterType {
    return sharedFilterAssigneeType;
  }

  get filterOwner(): OwnerFilterType {
    return sharedFilterOwner;
  }

  get selectedItem(): BoardItem | null {
    return sharedSelectedItem;
  }

  get projects(): string[] {
    const projectSet = new Set(sharedItems.map(item => item.sourceProject));
    return [...projectSet].sort();
  }

  isOwnItem(item: BoardItem, currentUserId: number | undefined): boolean {
    if (!currentUserId || !item.userId) return true;
    return item.userId === currentUserId;
  }

  itemsByColumn(column: BoardColumn): BoardItem[] {
    return this.filteredItems
      .filter(item => item.boardColumn === column)
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
  }

  setFilterProject(project: string | null): void {
    sharedFilterProject = project;
    notifyHosts();
  }

  setFilterAssigneeType(type: AssigneeFilterType): void {
    sharedFilterAssigneeType = type;
    notifyHosts();
  }

  setFilterOwner(owner: OwnerFilterType): void {
    sharedFilterOwner = owner;
    notifyHosts();
  }

  selectItem(item: BoardItem): void {
    sharedSelectedItem = item;
    notifyHosts();
  }

  clearSelection(): void {
    sharedSelectedItem = null;
    notifyHosts();
  }

  updateItemBoardColumn(itemId: string, column: BoardColumn): void {
    updateItemExecutionFields(itemId, { boardColumn: column });
  }

  async load(): Promise<void> {
    await loadBoardItems();
  }

  async refresh(): Promise<void> {
    await this.load();
  }

  isSpecExpanded(itemId: string): boolean {
    return expandedSpecs.has(itemId);
  }

  getSpecStories(itemId: string): StorySummary[] | undefined {
    return expandedSpecs.get(itemId);
  }

  isSpecStoriesLoading(itemId: string): boolean {
    return expandedSpecsLoading.has(itemId);
  }

  async toggleSpecExpanded(item: BoardItem): Promise<void> {
    if (expandedSpecs.has(item.id)) {
      expandedSpecs.delete(item.id);
      notifyHosts();
      return;
    }

    // Parse spec:{projectId}:{specId} from item.id
    const parts = item.id.split(':');
    if (parts.length < 3 || parts[0] !== 'spec') return;
    const projectId = Number(parts[1]);
    const specId = parts.slice(2).join(':');
    if (Number.isNaN(projectId)) return;

    expandedSpecsLoading.add(item.id);
    notifyHosts();

    try {
      const stories = await boardApi.getSpecStories(projectId, specId);
      expandedSpecs.set(item.id, stories);
    } catch {
      expandedSpecs.set(item.id, []);
    } finally {
      expandedSpecsLoading.delete(item.id);
      notifyHosts();
    }
  }

  isChildrenExpanded(itemId: string): boolean {
    return expandedChildren.has(itemId);
  }

  getLinkedChildren(item: BoardItem): SpecSummary[] {
    return item.children ?? [];
  }

  async toggleExpand(item: BoardItem): Promise<void> {
    if (item.sourceType === 'spec') {
      await this.toggleSpecExpanded(item);
    } else {
      if (expandedChildren.has(item.id)) {
        expandedChildren.delete(item.id);
      } else {
        expandedChildren.add(item.id);
      }
      notifyHosts();
    }
  }
}
